import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, kindLabel, microsToCredits, shortHex } from "../api";
import { ageLabel, ageMs, cached } from "../observation";
import { useObservation } from "../observations";
import { useResource } from "../resources";
import DagViz from "../dag-viz";

const DEFAULT_DAG_SIZE = { width: 800, height: 600, minHeight: 400, viewportOffset: 240 };

export default function DagPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const { data: selectedTx, error: selectedError } = useResource(selected ?? undefined, api.tx);
  const [since, setSince] = useState(0);
  const [limit, setLimit] = useState(200);
  const read = useMemo(() => (signal: AbortSignal) => api.dag(since, limit, signal), [since, limit]);
  const observation = useObservation(read);
  const slice = observation.data;
  const err = observation.error;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: DEFAULT_DAG_SIZE.width, h: DEFAULT_DAG_SIZE.height });

  useEffect(() => {
    function measure() {
      const el = wrapRef.current;
      if (!el) return;
      setSize({ w: el.clientWidth, h: Math.max(DEFAULT_DAG_SIZE.minHeight, window.innerHeight - DEFAULT_DAG_SIZE.viewportOffset) });
    }
    const observer = new ResizeObserver(measure);
    if (wrapRef.current) observer.observe(wrapRef.current);
    measure();
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-mesh-accent w-full sm:w-auto sm:flex-1">
          DAG visualizer
        </h1>
        <label className="text-sm text-mesh-mute flex items-center gap-2">
          since round
          <input
            type="number"
            value={since}
            onChange={(e) => setSince(Number(e.target.value))}
            className="w-20 px-2 py-1 rounded bg-mesh-panel"
          />
        </label>
        <label className="text-sm text-mesh-mute flex items-center gap-2">
          limit
          <input
            type="number"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-24 px-2 py-1 rounded bg-mesh-panel"
          />
        </label>
      </div>
      <div className="text-sm text-mesh-mute">
        {slice
          ? `${cached(observation) ? "Cached DAG" : "DAG observed"} ${ageLabel(ageMs(observation))} · ${slice.nodes.length} node${slice.nodes.length === 1 ? "" : "s"}`
          : err ? "DAG unavailable — retrying" : "Loading DAG…"}
      </div>
      {err && (
        <div className="bg-mesh-err/20 text-mesh-err p-3 rounded text-sm">
          {err}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          ref={wrapRef}
          className="lg:col-span-2 bg-mesh-bg rounded-lg border border-mesh-panel overflow-hidden"
          style={{ minHeight: DEFAULT_DAG_SIZE.minHeight }}
        >
          {slice && slice.nodes.length > 0 ? (
            <DagViz
              key={`${since}:${limit}`}
              slice={slice}
              onSelect={setSelected}
              selected={selected}
              width={size.w}
              height={size.h}
            />
          ) : (
            <div className="p-8 text-center text-mesh-mute">
              {slice ? "No DAG records for these filters." : err ? "No successful DAG observation yet." : "Loading DAG…"}
            </div>
          )}
        </div>
        <aside className="min-w-0 bg-mesh-panel/40 rounded-lg p-4 space-y-3">
          <h2 className="text-sm uppercase tracking-wider text-mesh-mute">
            tx details
          </h2>
          {selected !== null && (
            <button onClick={() => setSelected(null)} className="text-xs text-mesh-accent hover:underline">
              Clear selection
            </button>
          )}
          {selected !== null && !selectedTx && (
            <div className="space-y-2 text-sm" role={selectedError ? "alert" : "status"}>
              <div className="hex break-all">{selected}</div>
              <div>{selectedError ? "Selected transaction unavailable" : "Loading selected transaction…"}</div>
              {selectedError && <div className="text-mesh-err">{selectedError}</div>}
            </div>
          )}
          {selected === null && (
            <div className="text-mesh-mute text-sm">
              click a node to inspect.
              <ul className="mt-3 space-y-1 text-xs">
                <li>
                  <span className="inline-block w-3 h-3 rounded-full bg-mesh-ok mr-2" />
                  FINAL (witness-confirmed)
                </li>
                <li>
                  <span className="inline-block w-3 h-3 rounded-full bg-mesh-warn mr-2" />
                  TENTATIVE (in DAG, awaiting witness)
                </li>
                <li>
                  <span className="inline-block w-3 h-3 rounded-full bg-mesh-err mr-2" />
                  INVALID (double-spend or cascade)
                </li>
              </ul>
            </div>
          )}
          {selected !== null && selectedTx && (
            <div className="space-y-2 text-sm">
              <div>
                <div className="text-xs text-mesh-mute">tx_id</div>
                <Link
                  to={`/tx/${selectedTx.tx_id}`}
                  className="block hex break-all text-mesh-accent hover:underline"
                >
                  {selectedTx.tx_id}
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-mesh-mute">kind</div>
                  <div>{kindLabel(selectedTx.kind)}</div>
                </div>
                <div>
                  <div className="text-xs text-mesh-mute">status</div>
                  <div>{selectedTx.status}</div>
                </div>
                <div>
                  <div className="text-xs text-mesh-mute">round</div>
                  <div>
                    {selectedTx.witness_round != null
                      ? `r${selectedTx.witness_round}`
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-mesh-mute">amount</div>
                  <div>
                    {selectedTx.kind === 0
                      ? `${microsToCredits(selectedTx.amount)} cr`
                      : "—"}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs text-mesh-mute">sender → receiver</div>
                <div className="hex text-xs">
                  {shortHex(selectedTx.sender)} → {shortHex(selectedTx.receiver)}
                </div>
              </div>
              <div>
                <div className="text-xs text-mesh-mute">parents</div>
                <ul className="text-xs space-y-0.5">
                  {selectedTx.parents.map((p) => (
                    <li key={p}>
                      <button
                        title={p}
                        onClick={() => setSelected(p)}
                        className="block hex break-all text-mesh-accent hover:underline"
                      >
                        {shortHex(p, 10, 6)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
